import { transferChannelLabel } from '@/lib/format/transfer'
import type { ChannelActivityEvent } from '@/types/SocketEvents'
import { safeContactName } from './chatTitle'
import type { ChannelActivityPref, ChannelActivityPrefs } from './prefs'

export interface ChannelActivityContext {
  /** Canal aberto na rota atual (`channelIdFromPathname`), `null` fora de canal. */
  currentChannelId: string | null
  /** `document.visibilityState === 'visible'`. */
  tabVisible: boolean
  prefs: ChannelActivityPrefs
}

/**
 * Qual preferência governa este evento. `null` = evento que **nunca** toasta, e o
 * caso é um só: mensagem no canal ABERTO com a aba VISÍVEL. A mensagem já está na
 * tela — toast aí é ruído puro, não configuração, e por isso não vira chave.
 */
const KINDS_CONHECIDOS = new Set(['new_message', 'protocol_moved_in', 'protocol_moved_out'])

/**
 * Fronteira: `channel_activity` é o único payload desta feature que atravessa sem
 * parser (os contadores têm `parseChannelCounters`). Sem esta guarda, um `kind` que
 * o back acrescente depois — e ele já acrescentou `transfer_kind` sem ninguém pedir —
 * seria classificado como `new_message` por `channelActivityPrefKey` e lido como
 * transferência por `channelActivityMessage`, estourando em `evt.other_channel`
 * DENTRO do flush. Erro em `setTimeout` não é capturado: abortaria o loop e os grupos
 * seguintes do mesmo lote perderiam o toast, com o buffer já zerado.
 */
export function isKnownChannelActivity(evt: ChannelActivityEvent): boolean {
  return KINDS_CONHECIDOS.has(evt.kind)
}

export function channelActivityPrefKey(
  evt: ChannelActivityEvent,
  ctx: Pick<ChannelActivityContext, 'currentChannelId' | 'tabVisible'>,
): ChannelActivityPref | null {
  if (evt.kind === 'protocol_moved_in') return 'movedIn'
  if (evt.kind === 'protocol_moved_out') return 'movedOut'
  if (evt.channel_id !== ctx.currentChannelId) return 'newMessageOtherChannel'
  return ctx.tabVisible ? null : 'newMessageHidden'
}

export function shouldToastChannelActivity(
  evt: ChannelActivityEvent,
  ctx: ChannelActivityContext,
): boolean {
  const key = channelActivityPrefKey(evt, ctx)
  return key !== null && ctx.prefs[key]
}

/** Tipos de mídia que valem virar rótulo no toast. Tipo fora do mapa — inclusive
 *  `'unsupported'`, que a Meta manda de verdade — cai no genérico "Nova mensagem": o
 *  toast não é o lugar de explicar formato que o app nem renderiza. */
const MESSAGE_TYPE_LABELS: Record<string, string> = {
  image: 'Imagem',
  video: 'Vídeo',
  audio: 'Áudio',
  document: 'Documento',
  sticker: 'Figurinha',
  location: 'Localização',
  contacts: 'Contato',
}

/** Uma frase por evento. `channel_name`/`other_channel.name` `null` = canal apagado
 *  entre o fato e o emit; reusa o `transferChannelLabel` pra não haver dois
 *  vocabulários de "canal que não existe mais". */
export function channelActivityMessage(
  evt: ChannelActivityEvent,
  /** Como chamar o contato. O default identifica; a bandeja do SO passa o
   *  rótulo sanitizado (ver `channelActivitySystemLabel`). */
  contactLabel: string = evt.contact.name,
): string {
  const canal = transferChannelLabel(evt.channel_name)

  if (evt.kind === 'new_message') {
    const prefixo = MESSAGE_TYPE_LABELS[evt.message_type] ?? 'Nova mensagem'
    return `${prefixo} de ${contactLabel} em ${canal}`
  }

  const outro = transferChannelLabel(evt.other_channel.name)

  // `transfer_kind === 'auto'` é o ÚNICO sinal confiável de automático.
  // `by.username === 'System'` sai por três caminhos distintos e também aparece
  // quando o ator simplesmente não tem `username` — imprimir "System transferiu"
  // seria mentir em dois dos três.
  if (evt.transfer_kind === 'auto') {
    return evt.kind === 'protocol_moved_in'
      ? `${contactLabel} foi transferido automaticamente para ${canal}`
      : `${contactLabel} foi transferido automaticamente de ${canal} para ${outro}`
  }

  // Roubo tem verbo próprio nas duas superfícies que já o mostram
  // (`useChatMoved.ts:15`, `TransferMarker.tsx:23`) — o toast segue o mesmo.
  // Não leva canal: o verbo descreve o ato, e nomear o canal inverteria o
  // sujeito no `_out`.
  if (evt.transfer_kind === 'takeover') {
    return `${evt.by.username} assumiu o atendimento de ${contactLabel}`
  }

  return evt.kind === 'protocol_moved_in'
    ? `${evt.by.username} transferiu ${contactLabel} para ${canal}`
    : `${evt.by.username} transferiu ${contactLabel} de ${canal} para ${outro}`
}

/**
 * Texto do toast de um grupo já agregado por canal + kind. O back **não** coalesce
 * (divergência D1, com razão medida), então rajada chega evento a evento e o
 * agrupamento é decisão do front — sem isso, N mensagens = N toasts. Precedente:
 * `useBulkSendResultToast`.
 */
export function summarizeChannelActivity(events: ChannelActivityEvent[]): string {
  const [primeiro] = events
  if (events.length === 1) return channelActivityMessage(primeiro)

  const canal = transferChannelLabel(primeiro.channel_name)
  if (primeiro.kind === 'new_message') return `${events.length} novas mensagens em ${canal}`
  return primeiro.kind === 'protocol_moved_in'
    ? `${events.length} atendimentos transferidos para ${canal}`
    : `${events.length} atendimentos transferidos de ${canal}`
}

/**
 * Como chamar o contato quando a frase vai para a BANDEJA DO SO.
 *
 * O payload não tem campo de telefone, mas `contact.name` PODE SER um: o back usa
 * `fallback_name = phone_number or user_id` quando o perfil do WhatsApp não expõe
 * nome. Era essa a razão pela qual a API do SO ficou fora de escopo aqui — e a
 * razão caiu quando `chatTitle` passou a resolver exatamente isso, por FORMA e
 * não por comparação. Reusa `safeContactName`; não repete a heurística.
 *
 * O rótulo anônimo é necessário porque, ao contrário do título de `chatTitle`,
 * aqui o nome fica no MEIO da frase ("maria transferiu ___ para Vendas") e não há
 * cláusula final para simplesmente omitir.
 */
const CONTATO_SEM_NOME = 'um contato'

export function channelActivitySystemLabel(evt: ChannelActivityEvent): string {
  return safeContactName({ name: evt.contact.name }) ?? CONTATO_SEM_NOME
}

/**
 * O MESMO texto do toast, com o contato sanitizado para a bandeja.
 *
 * Grupo com mais de um evento não passa por sanitização nenhuma porque o texto
 * agregado já não nomeia contato ("3 novas mensagens em Vendas") — o que sai daqui
 * é sempre o vocabulário de `summarizeChannelActivity`, nunca um segundo.
 */
export function summarizeChannelActivityForSystem(events: ChannelActivityEvent[]): string {
  const [primeiro] = events
  if (events.length === 1) {
    return channelActivityMessage(primeiro, channelActivitySystemLabel(primeiro))
  }
  return summarizeChannelActivity(events)
}

/**
 * Identidade da entrada na bandeja (`tag`) e destino do clique (`url`), para um
 * grupo já agregado.
 *
 * **A `tag` é o que impede a bandeja de virar uma pilha.** Mesma `tag` substitui
 * em vez de empilhar, e a janela de 2s deste hook resolve a rajada só do lado do
 * toast: sem `tag`, cada janela deixaria mais uma entrada no SO.
 *
 * Grupo de uma conversa só usa o `protocol_id`, e isso é deliberado nos dois
 * sentidos: o clique abre a CONVERSA (mesmo formato de [[../../hooks/useNotification]]),
 * e a `tag` é a MESMA que o `ChannelContext` usa (`chatId: updatedChat._id` em
 * `ChannelContext.tsx:289`, que vira `tag` em `useNotification.ts:71`; o `_id` do
 * chat é o `protocol_id` — `ChannelContext.tsx:141` casa um contra o outro no
 * `REMOVE_CHAT`), então
 * nem no caso limite os dois produtores deixam duas entradas do mesmo fato.
 *
 * Grupo com várias conversas não tem conversa para abrir: cai no canal, com o
 * `kind` na chave para que uma transferência não substitua um aviso de mensagem.
 */
export function channelActivityTarget(events: ChannelActivityEvent[]): {
  tag: string
  url: string
} {
  const [primeiro] = events
  const protocolos = new Set(events.map((e) => e.protocol_id))

  if (protocolos.size === 1) {
    return {
      tag: primeiro.protocol_id,
      url: `/chatbot/${primeiro.channel_id}/${primeiro.protocol_id}`,
    }
  }
  return {
    tag: `canal:${primeiro.channel_id}|${primeiro.kind}`,
    url: `/chatbot/${primeiro.channel_id}`,
  }
}

/**
 * Se o aviso do SO deste evento é de quem escuta `channel_activity` — ou do
 * `ChannelProvider` da rota, que vê a MESMA mensagem por `channel_update_chats`.
 *
 * **`newMessageHidden` é o único caso em que os dois produtores enxergam o mesmo
 * fato**, e ele é do `ChannelProvider` por duas razões:
 *
 * 1. **Ele está montado, por construção.** A chave `newMessageHidden` exige
 *    `evt.channel_id === currentChannelId`, e `currentChannelId` só é não-nulo em
 *    `/chatbot/[channelId]` (`channelIdFromPathname`) — que é exatamente onde o
 *    `ChannelProvider` monta (`chatbot/[channelId]/layout.tsx:30`).
 * 2. **Ele avisa melhor**: `channel_update_chats` traz `last_message`, então o
 *    aviso dele tem a prévia do texto. Este payload não carrega o corpo da
 *    mensagem e não vai inventá-lo.
 *
 * Os demais casos não têm concorrente: `newMessageOtherChannel` é de um canal que
 * o `ChannelProvider` da rota não escuta (ele é escopado por `key={channelId}`), e
 * transferência ele não anuncia em kind nenhum.
 *
 * **A cessão é INTEGRAL desde 2026-09-04**: quem cede não toasta, não toca som e
 * não notifica. Duas coisas mudaram para isso valer, e as duas foram achado do
 * review final:
 *
 * - Antes, quem cedia ainda toastava **incondicionalmente**, sem olhar o
 *   `outcome` do outro produtor. Janela minimizada: o provider mandava a
 *   notificação do SO, este hook toastava na janela escondida, e o operador
 *   clicava na notificação e encontrava o toast do mesmo fato ao restaurar —
 *   o duplo que a Task 11 e o D-2 proíbem.
 * - Antes, o `ChannelProvider` se abstinha quando a CONVERSA estava aberta na
 *   rota (guard de URL), e com a aba escondida o fato ficava sem aviso nenhum:
 *   celular bloqueado na conversa, mensagem chegando, silêncio dos dois lados.
 *   Hoje ele trata rota-com-aba-escondida como "não está olhando" e avisa.
 *
 * Herdar o caso significou herdar a preferência junto: o provider consulta
 * `channelActivity.newMessageHidden` antes de avisar, senão desligá-la na tela
 * de notificações não silenciaria mais nada.
 */
export function shouldNotifySystemForChannelActivity(prefKey: ChannelActivityPref): boolean {
  return prefKey !== 'newMessageHidden'
}
